import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { readCatalogCache, writeCatalogCache, readQueue, writeQueue, pushFailedSale } from "@/lib/catalogCache";
import { getPrintSettings, kickDrawer } from "@/lib/printer";
import { useAuth } from "@/context/AuthContext";
import { rupiah, FASMART_CATS, branchStock, branchPrice } from "@/lib/format";
import BarcodeScanner from "@/components/BarcodeScanner";
import KasirPanel from "@/components/KasirPanel";
import { requestPin, PinGateModal } from "@/components/PinGate";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Printer, Search, ArrowLeft, Receipt, X, Save } from "lucide-react";

const CH_CHANNELS = [["bri_link", "BRI Link"], ["order", "Order Kuota"], ["bsi", "BSI"], ["seabank", "Seabank"], ["tf_abet", "TF dari Pak Abet"]];
const CH_JENIS = ["TTA", "TTTF", "TTF", "Pulsa", "Dana", "Gopay", "OVO", "ShopeePay", "Kuota", "Token PLN", "Belanja", "Lainnya"];
const todayWIB = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
const emptyChForm = { channel: "bri_link", arah: "in", jenis: "TTA", catatan: "", amount: "", admin_fee: "" };

export default function Kasir() {
  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState({});
  const [payment, setPayment] = useState("tunai");
  const [cash, setCash] = useState("");
  const [phone, setPhone] = useState("");
  const [member, setMember] = useState(null);
  const [today, setToday] = useState({ sales: [], total: 0, count: 0 });
  const [lastSale, setLastSale] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [chEntries, setChEntries] = useState([]);
  const [chForm, setChForm] = useState(emptyChForm);
  const [chDraft, setChDraft] = useState([]);
  const [panel, setPanel] = useState(null);
  const [newOrders, setNewOrders] = useState(0);
  const knownOrderIds = useRef(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(() => readQueue().length);
  const searchRef = useRef(null);
  const focusSearch = () => setTimeout(() => searchRef.current?.focus(), 60);
  const [autoPrinted, setAutoPrinted] = useState(false);

  const afterSalePrint = (sale) => {
    const s = getPrintSettings();
    if (s.autoprint) {
      setAutoPrinted(true);
      setTimeout(() => window.print(), 600);
      toast.success("🖨 Struk otomatis dikirim ke printer — tidak perlu klik Cetak lagi", { duration: 4000 });
    }
    if (s.drawer_kick && sale.payment_method === "tunai") kickDrawer();
  };

  const saveNoPrint = () => {
    if (lastSale?.payment_method === "tunai" && getPrintSettings().drawer_kick) kickDrawer();
    toast.success("Transaksi tersimpan — struk tidak dicetak");
    setLastSale(null);
  };

  const notifyNewOrder = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [880, 1174, 1568].forEach((f, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.value = f;
        const t = ctx.currentTime + i * 0.18;
        gain.gain.setValueAtTime(0.001, t);
        gain.gain.exponentialRampToValueAtTime(0.3, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16);
        osc.start(t);
        osc.stop(t + 0.18);
      });
    } catch {}
  };

  const effectiveBranch = user?.branch_id || branchId;

  const applyCatalog = (list) => setProducts(list.filter((p) => FASMART_CATS.includes(p.category)));
  const loadCatalog = (attempt = 0) => {
    api.get("/products")
      .then((r) => { writeCatalogCache(r.data); applyCatalog(r.data); })
      .catch(() => { if (attempt < 4) setTimeout(() => loadCatalog(attempt + 1), 1200 * (attempt + 1)); });
  };

  useEffect(() => {
    const cached = readCatalogCache();
    if (cached) applyCatalog(cached); // katalog tampil instan walau offline
    loadCatalog();
    api.get("/branches").then((r) => {
      setBranches(r.data);
      if (r.data.length && !branchId) setBranchId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const buildReceiptPrintRoot = () => {
    const src = document.getElementById("receipt-area");
    if (!src) return;
    document.getElementById("receipt-print-root")?.remove();
    const root = document.createElement("div");
    root.id = "receipt-print-root";
    root.className = "font-mono text-xs text-stone-800";
    root.innerHTML = src.innerHTML;
    document.body.appendChild(root);
    document.body.classList.add("printing-receipt");
  };
  const removeReceiptPrintRoot = () => {
    document.getElementById("receipt-print-root")?.remove();
    document.body.classList.remove("printing-receipt");
  };
  useEffect(() => {
    window.addEventListener("beforeprint", buildReceiptPrintRoot);
    window.addEventListener("afterprint", removeReceiptPrintRoot);
    window.addEventListener("pointerdown", removeReceiptPrintRoot);
    return () => {
      window.removeEventListener("beforeprint", buildReceiptPrintRoot);
      window.removeEventListener("afterprint", removeReceiptPrintRoot);
      window.removeEventListener("pointerdown", removeReceiptPrintRoot);
      removeReceiptPrintRoot();
    };
  }, []);

  const loadToday = () => {
    if (!effectiveBranch) return;
    api.get(`/pos/sales/today?branch_id=${effectiveBranch}`).then((r) => setToday(r.data)).catch(() => {});
    api.get(`/finance/report?branch_id=${effectiveBranch}&date=${todayWIB()}`).then((r) => setChEntries(r.data.report?.entries || [])).catch(() => {});
  };
  useEffect(() => { loadToday(); }, [effectiveBranch]);

  // keranjang tersimpan otomatis per cabang — aman saat refresh / pindah input stok
  const CART_KEY = `fas_kasir_cart_${effectiveBranch || "default"}`;
  const cartRestored = useRef("");
  useEffect(() => {
    if (!effectiveBranch || cartRestored.current === CART_KEY) return;
    cartRestored.current = CART_KEY;
    try {
      const c = JSON.parse(localStorage.getItem(CART_KEY) || "{}");
      if (c && typeof c === "object" && Object.keys(c).length) { setCart(c); toast.info(`Keranjang tersimpan dipulihkan (${Object.keys(c).length} item)`); }
    } catch {}
  }, [effectiveBranch]);
  useEffect(() => {
    if (!effectiveBranch || cartRestored.current !== CART_KEY) return;
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch {}
  }, [cart, effectiveBranch]);
  // segarkan harga & stok item keranjang begitu katalog termuat
  useEffect(() => {
    if (!products.length) return;
    setCart((c) => {
      let changed = false;
      const next = {};
      for (const [pid, it] of Object.entries(c)) {
        const fresh = products.find((p) => p.id === pid);
        if (!fresh) { changed = true; continue; }
        const price = branchPrice(fresh, effectiveBranch);
        const stock = branchStock(fresh, effectiveBranch);
        const qty = Math.min(it.qty, stock);
        if (qty <= 0) { changed = true; continue; }
        next[pid] = { product: { ...fresh, price }, qty };
        if (qty !== it.qty || price !== it.product.price) changed = true;
      }
      return changed ? next : c;
    });
  }, [products]);

  useEffect(() => { if (!lastSale && user?.role === "admin") focusSearch(); if (!lastSale) setAutoPrinted(false); }, [lastSale, loading]);

  const syncQueue = async () => {
    const q = readQueue();
    if (!q.length) { if (queueCount) setQueueCount(0); return; }
    if (!navigator.onLine) { setQueueCount(q.length); return; }
    const remaining = [];
    let synced = 0, failed = 0;
    for (let i = 0; i < q.length; i++) {
      try { await api.post("/pos/sales", q[i].payload); synced++; }
      catch (e) {
        if (!e.response) { remaining.push(...q.slice(i)); break; } // koneksi masih mati — sisa tetap antre
        failed++;
        pushFailedSale(q[i]); // server menolak (4xx) — jangan diulang terus
      }
    }
    writeQueue(remaining);
    setQueueCount(remaining.length);
    if (synced) { toast.success(`🔄 ${synced} struk offline tersinkron ke server`); loadToday(); loadCatalog(); }
    if (failed) toast.error(`${failed} struk offline ditolak server (data berubah) — laporkan ke owner`);
  };

  useEffect(() => {
    const onOnline = () => { setOnline(true); syncQueue(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    syncQueue();
    const t = setInterval(syncQueue, 15000);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); clearInterval(t); };
  }, [effectiveBranch]);

  useEffect(() => {
    if (!user || user.role !== "admin" || !effectiveBranch) return;
    let alive = true;
    const check = async () => {
      try {
        const r = await api.get("/orders");
        if (!alive) return;
        const fresh = r.data.filter((o) => o.branch_id === effectiveBranch && o.status === "baru");
        setNewOrders(fresh.length);
        const ids = new Set(fresh.map((o) => o.id));
        if (knownOrderIds.current === null) { knownOrderIds.current = ids; return; }
        const incoming = fresh.filter((o) => !knownOrderIds.current.has(o.id));
        knownOrderIds.current = ids;
        if (incoming.length) {
          notifyNewOrder();
          toast(`🛎 ${incoming.length} pesanan online baru masuk!`, { description: incoming.map((o) => `${o.customer_name || "Pelanggan"} · ${rupiah(o.total)}`).join("\n"), duration: 10000 });
        }
      } catch {}
    };
    check();
    const t = setInterval(check, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [effectiveBranch, user]);

  const addChannelToDraft = () => {
    const amount = parseInt(chForm.amount || "0", 10) || 0;
    if (amount <= 0) { toast.error("Nominal wajib diisi"); return; }
    setChDraft([...chDraft, { channel: chForm.channel, arah: chForm.arah, jenis: chForm.jenis, catatan: chForm.catatan.trim(), amount, admin_fee: parseInt(chForm.admin_fee || "0", 10) || 0 }]);
    setChForm(emptyChForm);
    toast.success("Transaksi channel masuk struk — tercatat ke Laporan Keuangan saat Bayar");
  };

  const delChannel = async (e) => {
    const pin = await requestPin();
    if (!pin) return;
    try {
      const r = await api.post("/finance/channel-entry/delete", {
        branch_id: effectiveBranch,
        entry_id: e.id || "",
        match: { channel: e.channel, arah: e.arah, jenis: e.jenis, catatan: e.catatan, amount: e.amount, admin_fee: e.admin_fee },
        pin,
      });
      setChEntries(r.data.entries);
      toast.success("Baris transaksi dihapus");
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus"); }
  };

  const chIn = chEntries.filter((e) => e.arah === "in").reduce((s, e) => s + (e.amount || 0), 0);
  const chOut = chEntries.filter((e) => e.arah === "out").reduce((s, e) => s + (e.amount || 0), 0);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [products, search]);

  const items = Object.values(cart);
  const unitPrice = (it) => {
    const p = it.product;
    return p.grosir_price > 0 && p.grosir_min > 0 && it.qty >= p.grosir_min ? p.grosir_price : p.price;
  };
  const isGrosir = (it) => it.product.grosir_price > 0 && it.product.grosir_min > 0 && it.qty >= it.product.grosir_min;
  const productTotal = items.reduce((s, it) => s + unitPrice(it) * it.qty, 0);
  const chDraftBill = chDraft.reduce((s, e) => s + (e.arah === "in" ? e.amount + e.admin_fee : e.admin_fee), 0);
  const total = productTotal + chDraftBill;
  const chLabel = (id) => CH_CHANNELS.find(([v]) => v === id)?.[1] || id;
  const cashNum = parseInt(cash || "0", 10) || 0;
  const change = payment === "tunai" ? Math.max(0, cashNum - total) : 0;

  const add = (p) => {
    focusSearch();
    const price = branchPrice(p, effectiveBranch);
    const stock = branchStock(p, effectiveBranch);
    const cur = cart[p.id]?.qty || 0;
    if (cur + 1 > stock) { toast.error(`Stok ${p.name} sisa ${stock}`); return; }
    setCart((c) => ({ ...c, [p.id]: { product: { ...p, price }, qty: cur + 1 } }));
  };
  const dec = (p) => {
    const cur = cart[p.id]?.qty || 0;
    if (cur <= 1) { const { [p.id]: _, ...rest } = cart; setCart(rest); return; }
    setCart((c) => ({ ...c, [p.id]: { ...c[p.id], qty: cur - 1 } }));
  };
  const removeItem = async (id) => { const pin = await requestPin(); if (!pin) return; const { [id]: _, ...rest } = cart; setCart(rest); };

  const handleSearchEnter = (e) => {
    if (e.key !== "Enter") return;
    const q = search.trim().toLowerCase();
    if (!q) return;
    const exact = products.filter((p) => p.id.toLowerCase() === q || (p.barcode && String(p.barcode).toLowerCase() === q) || p.name.toLowerCase() === q);
    const match = exact.length === 1 ? exact[0] : filtered.length === 1 ? filtered[0] : null;
    if (match) { add(match); setSearch(""); }
  };

  const handleScan = (code) => {
    setShowScanner(false);
    const q = code.trim().toLowerCase();
    const p = products.find((x) => (x.barcode && String(x.barcode).toLowerCase() === q) || x.id.toLowerCase() === q);
    if (p) { add(p); toast.success(`${p.name} masuk keranjang`); }
    else { setSearch(code); toast.error(`Barcode ${code} belum terdaftar — silakan input dulu di Admin`); }
  };

  const checkMember = async () => {
    if (!phone.trim()) return;
    try {
      const r = await api.get(`/pos/member-lookup?phone=${encodeURIComponent(phone.trim())}`);
      if (r.data.found) { setMember(r.data); toast.success(`Member: ${r.data.name} (${rupiah(0).replace("0", "")}${r.data.points} poin)`); }
      else { setMember(null); toast.error("Nomor HP tidak terdaftar sebagai member"); }
    } catch { toast.error("Gagal memeriksa member"); }
  };

  const pay = async () => {
    if (!items.length && !chDraft.length) { toast.error("Keranjang kosong"); return; }
    if (payment === "tunai" && cashNum < total) { toast.error("Uang tunai kurang dari total"); return; }
    setSaving(true);
    const payload = {
      branch_id: effectiveBranch,
      items: items.map((it) => ({ product_id: it.product.id, qty: it.qty })),
      channel_entries: chDraft,
      payment_method: payment,
      cash_received: payment === "tunai" ? cashNum : total,
      member_phone: member ? phone.trim() : "",
      client_ref: `off_${crypto.randomUUID()}`,
    };
    try {
      const r = await api.post("/pos/sales", payload);
      setLastSale(r.data);
      afterSalePrint(r.data);
      toast.success(`Penjualan ${rupiah(r.data.total)} tercatat`);
      setCart({}); setCash(""); setPhone(""); setMember(null); setChDraft([]);
      loadToday(); loadCatalog();
    } catch (e) {
      if (!e.response) {
        // internet mati — struk disimpan ke antrean lokal, otomatis terkirim saat online lagi
        const q = readQueue();
        q.push({ payload, queued_at: new Date().toISOString() });
        writeQueue(q);
        setQueueCount(q.length);
        const localSale = {
          id: `local_${Date.now()}`, offline: true, branch_id: effectiveBranch, cashier_email: user.email,
          items: items.map((it) => ({ product_id: it.product.id, name: it.product.name, price: unitPrice(it), qty: it.qty, unit: it.product.unit })),
          total, product_total: productTotal,
          channel_entries: chDraft.map((e2) => ({ ...e2, billed: e2.arah === "in" ? e2.amount + e2.admin_fee : e2.admin_fee })),
          payment_method: payment, cash_received: payment === "tunai" ? cashNum : total,
          change: payment === "tunai" ? Math.max(0, cashNum - total) : 0,
          member_phone: payload.member_phone, member_name: member?.name || "", points_earned: member ? Math.floor(productTotal / 1000) : 0,
          created_at: new Date().toISOString(),
        };
        setLastSale(localSale);
        afterSalePrint(localSale);
        toast.warning(`📴 Internet mati — struk ${rupiah(total)} tersimpan offline, otomatis terkirim saat online`, { duration: 6000 });
        setCart({}); setCash(""); setPhone(""); setMember(null); setChDraft([]);
        // kurangi stok tampilan lokal agar tidak oversell selama sesi offline ini
        setProducts((ps) => ps.map((p) => {
          const it = payload.items.find((x) => x.product_id === p.id);
          if (!it) return p;
          const sbb = { ...(p.stock_by_branch || {}) };
          sbb[effectiveBranch] = Math.max(0, (sbb[effectiveBranch] || 0) - it.qty);
          return { ...p, stock_by_branch: sbb };
        }));
      } else {
        toast.error(e.response?.data?.detail || "Gagal mencatat penjualan");
      }
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Memuat...</div>;
  if (!user) return <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-stone-600"><p className="font-semibold">Masuk dulu untuk membuka kasir.</p><Link to="/" className="text-emerald-700 font-bold underline">Ke Beranda</Link></div>;
  if (user.role !== "admin") return <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-stone-600"><p className="font-semibold">Halaman kasir khusus admin toko.</p><Link to="/" className="text-emerald-700 font-bold underline">Ke Beranda</Link></div>;

  const branchName = branches.find((b) => b.id === effectiveBranch)?.name || "";

  return (
    <div data-testid="kasir-page" className="min-h-screen bg-stone-100 overflow-x-hidden">
      <header className="bg-white border-b border-[#E5DECF] px-4 py-3 flex items-center gap-3 flex-wrap sticky top-0 z-20">
        <Link to="/admin" data-testid="kasir-back-admin" className="flex items-center gap-1 text-sm font-semibold text-stone-600 hover:text-emerald-700"><ArrowLeft className="w-4 h-4" /> Admin</Link>
        <h1 className="font-display font-black text-lg text-stone-900">🧾 Kasir FASMART</h1>
        {user.branch_id ? (
          <span data-testid="kasir-branch-badge" className="tag-pill bg-amber-100 text-amber-800">{branchName}</span>
        ) : (
          <select data-testid="kasir-branch-select" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-1.5 bg-white">
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        {(!online || queueCount > 0) && (
          <span data-testid="kasir-offline-badge" className={`tag-pill ${online ? "bg-amber-100 text-amber-800" : "bg-rose-600 text-white animate-pulse"}`}>
            {online ? `🔄 ${queueCount} struk antre sinkron` : `📴 Offline · ${queueCount} struk antre`}
          </span>
        )}
        <div className="flex items-center gap-1 flex-wrap w-full sm:w-auto order-last sm:order-none" data-testid="kasir-quicknav">
          {[["ringkasan", "📊", "Ringkasan"], ["pesanan", "🧾", "Pesanan"], ["produk", "🏷", "Produk"], ["pelanggan", "👥", "Pelanggan"], ["laporan", "📈", "Laporan"], ["keuangan", "💰", "Keuangan"], ["pricetag", "🖨", "Tag Harga"], ["closing", "🔒", "Closing"], ...(!user.branch_id && !user.input_only ? [["import", "📥", "Import"]] : [])].map(([k, ic, l]) => (
            <button key={k} data-testid={`kasir-nav-${k}`} onClick={() => setPanel(k)} className={`relative px-2.5 py-1.5 rounded-full text-[11px] font-bold transition ${panel === k ? "bg-emerald-700 text-white" : "bg-white text-stone-600 hover:bg-emerald-50 border border-[#E5DECF]"}`}>{ic} <span className="hidden md:inline">{l}</span>{k === "pesanan" && newOrders > 0 && <span data-testid="kasir-nav-pesanan-badge" className="absolute -top-1.5 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-black flex items-center justify-center animate-pulse">{newOrders}</span>}</button>
          ))}
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 order-3 lg:order-none lg:row-start-1">
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input ref={searchRef} data-testid="kasir-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={handleSearchEnter} placeholder="Scan barcode / ketik nama produk, Enter = langsung tambah" className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-[#E5DECF] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/50" />
              {search && <button data-testid="kasir-search-clear" onClick={() => { setSearch(""); focusSearch(); }} title="Hapus pencarian/barcode" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 text-stone-500 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center transition"><X className="w-3 h-3" /></button>}
            </div>
            <button data-testid="kasir-scan-btn" onClick={() => setShowScanner(true)} title="Scan barcode via kamera" className="shrink-0 px-3.5 rounded-xl bg-stone-900 text-white text-sm font-bold hover:bg-emerald-700 transition">📷</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
            {filtered.map((p) => {
              const stok = branchStock(p, effectiveBranch);
              return (
                <button key={p.id} data-testid={`kasir-product-${p.id}`} onClick={() => add(p)} disabled={stok <= 0} className={`text-left bg-white rounded-xl border border-[#E5DECF] p-2.5 hover:border-emerald-600 transition ${stok <= 0 ? "opacity-40 cursor-not-allowed" : ""}`}>
                  <div className="text-xs font-bold text-stone-900 leading-tight line-clamp-2 min-h-[2rem]">{p.name}</div>
                  <div className="mt-1 text-emerald-800 font-display font-bold text-sm">{rupiah(branchPrice(p, effectiveBranch))}<span className="text-[10px] text-stone-400 font-normal">/{p.unit}</span></div>
                  {p.grosir_price > 0 && p.grosir_min > 0 && <div className="text-[9px] font-bold text-amber-600">Grosir {rupiah(p.grosir_price)} min {p.grosir_min}</div>}
                  <div className="text-[10px] text-stone-500 mt-0.5">Stok: <b>{stok}</b>{cart[p.id] ? ` · di keranjang: ${cart[p.id].qty}` : ""}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="contents lg:flex lg:flex-col lg:gap-4 lg:col-start-3 lg:row-start-1">
          <div data-testid="kasir-cart" className="order-1 lg:order-none bg-white rounded-2xl border border-[#E5DECF] p-4">
            <h2 className="font-display font-black text-base mb-3">Keranjang ({items.length})</h2>
            {items.length === 0 && <p className="text-sm text-stone-400">Klik produk untuk menambahkan.</p>}
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {items.map((it) => (
                <div key={it.product.id} className="flex items-center gap-2 text-sm">
                  <div className="flex-1 min-w-0"><div className="font-semibold truncate">{it.product.name} {isGrosir(it) && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 align-middle">GROSIR</span>}</div><div className="text-xs text-stone-500">{rupiah(unitPrice(it))} × {it.qty} = <b className="text-stone-700">{rupiah(unitPrice(it) * it.qty)}</b></div></div>
                  <button data-testid={`kasir-dec-${it.product.id}`} onClick={() => dec(it.product)} className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                  <button data-testid={`kasir-inc-${it.product.id}`} onClick={() => add(it.product)} className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                  <button data-testid={`kasir-del-${it.product.id}`} onClick={() => removeItem(it.product.id)} className="w-7 h-7 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            {chDraft.length > 0 && (
              <div className="mt-2 border-t border-dashed pt-2" data-testid="kasir-chdraft">
                <div className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mb-1">Transaksi Channel di struk ini</div>
                <div className="space-y-1">
                  {chDraft.map((e, i) => (
                    <div key={i} data-testid={`kasir-chdraft-${i}`} className="flex items-center gap-1.5 text-xs">
                      <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${e.arah === "in" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"}`}>{e.arah === "in" ? "IN" : "OUT"}</span>
                      <span className="flex-1 min-w-0 truncate text-stone-600">{chLabel(e.channel)} · {e.jenis}{e.arah === "out" ? ` · serah tunai ${rupiah(e.amount)}` : ""}{e.admin_fee ? ` · adm ${rupiah(e.admin_fee)}` : ""}</span>
                      <b>{rupiah(e.arah === "in" ? e.amount + e.admin_fee : e.admin_fee)}</b>
                      <button data-testid={`kasir-chdraft-del-${i}`} onClick={async () => { if (!(await requestPin())) return; setChDraft(chDraft.filter((_, j) => j !== i)); }} className="shrink-0 w-6 h-6 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="border-t border-dashed mt-3 pt-3 flex justify-between font-display font-black text-lg"><span>Total</span><span data-testid="kasir-total" className="text-emerald-800">{rupiah(total)}</span></div>

            <div className="mt-3">
              <div className="text-xs font-semibold text-stone-500 mb-1.5">Pembayaran</div>
              <div className="flex gap-1.5">
                {[["tunai", "Tunai"], ["qris", "QRIS"], ["transfer", "Transfer"]].map(([v, l]) => (
                  <button key={v} data-testid={`kasir-pay-${v}`} onClick={() => setPayment(v)} className={`flex-1 py-2 rounded-full text-xs font-bold border transition ${payment === v ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-[#E5DECF]"}`}>{l}</button>
                ))}
              </div>
            </div>
            {payment === "tunai" && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-stone-500 mb-1">Uang diterima (Rp)</div>
                <input data-testid="kasir-cash-input" type="number" value={cash} onChange={(e) => setCash(e.target.value)} className="w-full border border-[#E5DECF] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/50" placeholder="0" />
                <div className="flex gap-1.5 mt-1.5">
                  <button data-testid="kasir-cash-pas" onClick={() => setCash(String(total))} className="px-2.5 py-1.5 rounded-lg bg-emerald-100 text-emerald-800 text-[11px] font-bold hover:bg-emerald-200 transition">Pas</button>
                  {[50000, 100000, 150000, 200000].map((n) => (
                    <button key={n} data-testid={`kasir-cash-${n}`} onClick={() => setCash(String(n))} className="px-2.5 py-1.5 rounded-lg bg-stone-100 text-stone-700 text-[11px] font-bold hover:bg-stone-200 transition">{n / 1000}rb</button>
                  ))}
                </div>
                <div className="mt-1.5 flex justify-between text-sm"><span className="text-stone-500">Kembalian</span><b data-testid="kasir-change" className="text-amber-700">{rupiah(change)}</b></div>
              </div>
            )}

            <div className="mt-3">
              <div className="text-xs font-semibold text-stone-500 mb-1">No. HP Member (opsional, untuk poin)</div>
              <div className="flex gap-1.5">
                <input data-testid="kasir-member-phone" value={phone} onChange={(e) => { setPhone(e.target.value); setMember(null); }} className="flex-1 border border-[#E5DECF] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/50" placeholder="08xx" />
                <button data-testid="kasir-member-check" onClick={checkMember} className="px-3 py-2 rounded-xl bg-stone-900 text-white text-xs font-bold">Cek</button>
              </div>
              {member && <div data-testid="kasir-member-info" className="mt-1.5 text-xs text-emerald-800 font-semibold">✓ {member.name} · {member.points} poin · {member.tier} · akan dapat +{Math.floor(productTotal / 1000)} poin</div>}
            </div>

            <button data-testid="kasir-pay-btn" onClick={pay} disabled={saving || (!items.length && !chDraft.length)} className="mt-4 w-full py-3 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-display font-bold text-base transition disabled:opacity-50">
              {saving ? "Menyimpan..." : `Bayar ${rupiah(total)}`}
            </button>
          </div>

          <div data-testid="kasir-channel" className="order-2 lg:order-none bg-white rounded-2xl border border-[#E5DECF] p-4">
            <h2 className="font-display font-black text-base mb-0.5">💳 Transaksi Channel</h2>
            <p className="text-[10px] text-stone-400 mb-2.5">BRI Link / BSI / Kuota dll — masuk ke struk belanja (1 transaksi 1 struk) & tercatat di Laporan Keuangan saat Bayar</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-xs">
              <select data-testid="kasir-ch-channel" value={chForm.channel} onChange={(e) => setChForm({ ...chForm, channel: e.target.value })} className="border border-[#E5DECF] rounded-lg px-2 py-1.5 bg-white">
                {CH_CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select data-testid="kasir-ch-arah" value={chForm.arah} onChange={(e) => setChForm({ ...chForm, arah: e.target.value })} className="border border-[#E5DECF] rounded-lg px-2 py-1.5 bg-white">
                <option value="in">Masuk</option>
                <option value="out">Keluar</option>
              </select>
              <select data-testid="kasir-ch-jenis" value={chForm.jenis} onChange={(e) => setChForm({ ...chForm, jenis: e.target.value })} className="border border-[#E5DECF] rounded-lg px-2 py-1.5 bg-white">
                {CH_JENIS.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
              <input data-testid="kasir-ch-amount" type="number" value={chForm.amount} onChange={(e) => setChForm({ ...chForm, amount: e.target.value })} placeholder="Nominal *" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
              <input data-testid="kasir-ch-admin" type="number" value={chForm.admin_fee} onChange={(e) => setChForm({ ...chForm, admin_fee: e.target.value })} placeholder="Admin (Rp)" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
              <input data-testid="kasir-ch-catatan" value={chForm.catatan} onChange={(e) => setChForm({ ...chForm, catatan: e.target.value })} placeholder="Catatan" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
            </div>
            <button data-testid="kasir-ch-save" onClick={addChannelToDraft} className="mt-2 w-full py-2.5 rounded-full bg-stone-900 hover:bg-emerald-700 text-white text-xs font-bold transition">
              + Tambah ke Struk
            </button>
            <div data-testid="kasir-ch-totals" className="mt-2.5 flex justify-between text-xs">
              <span className="text-stone-500">Hari ini: <b className="text-emerald-700">Masuk {rupiah(chIn)}</b> · <b className="text-rose-600">Keluar {rupiah(chOut)}</b></span>
              <span className="text-stone-400">{chEntries.length} baris</span>
            </div>
            <div className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
              {chEntries.map((e, i) => (
                <div key={e.id || i} data-testid={`kasir-ch-row-${i}`} className="flex items-center gap-1.5 text-xs border-b border-dashed pb-1">
                  <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${e.arah === "in" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"}`}>{e.arah === "in" ? "IN" : "OUT"}</span>
                  <span className="flex-1 min-w-0 truncate text-stone-600">{CH_CHANNELS.find(([v]) => v === e.channel)?.[1] || e.channel} · {e.jenis}{e.catatan ? ` · ${e.catatan}` : ""}{e.admin_fee ? ` · adm ${rupiah(e.admin_fee)}` : ""}</span>
                  <b>{rupiah(e.amount)}</b>
                  <button data-testid={`kasir-ch-del-${i}`} onClick={() => delChannel(e)} className="shrink-0 w-6 h-6 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              {!chEntries.length && <p className="text-xs text-stone-400">Belum ada transaksi channel hari ini.</p>}
            </div>
          </div>

          <div data-testid="kasir-today-list" className="order-4 lg:order-none bg-white rounded-2xl border border-[#E5DECF] p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-black text-base flex items-center gap-1.5"><Receipt className="w-4 h-4" /> Penjualan Hari Ini</h2>
              <button onClick={loadToday} className="text-xs text-emerald-700 font-bold">Refresh</button>
            </div>
            <div className="text-sm font-bold text-emerald-800 mb-2" data-testid="kasir-today-total">{today.count} transaksi · {rupiah(today.total)}</div>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {today.sales.map((s, i) => (
                <div key={s.id} data-testid={`kasir-today-row-${i}`} className="flex items-center justify-between gap-1.5 text-xs border-b border-dashed pb-1.5">
                  <span className="text-stone-500 flex-1 min-w-0 truncate">{new Date(s.created_at).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" })} · {s.items.length} item · {s.payment_method}{s.member_name ? ` · ${s.member_name}` : ""}</span>
                  <b>{rupiah(s.total)}</b>
                  <button data-testid={`kasir-reprint-${i}`} onClick={() => setLastSale(s)} title="Print ulang struk" className="shrink-0 w-6 h-6 rounded-full bg-stone-100 text-stone-600 hover:bg-emerald-100 hover:text-emerald-700 transition flex items-center justify-center"><Printer className="w-3 h-3" /></button>
                </div>
              ))}
              {!today.sales.length && <p className="text-xs text-stone-400">Belum ada penjualan hari ini.</p>}
            </div>
          </div>
        </div>
      </div>

      {showScanner && <BarcodeScanner onResult={handleScan} onClose={() => setShowScanner(false)} />}
      {panel && <KasirPanel section={panel} branchId={effectiveBranch} branchName={branchName} onClose={() => setPanel(null)} />}
      <PinGateModal />

      {lastSale && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setLastSale(null)}>
          <div data-testid="kasir-receipt-modal" className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div id="receipt-area" className="font-mono text-xs text-stone-800">
              <div className="text-center mb-2"><div className="font-black text-sm">FASMART</div><div>{branchName}</div><div>Belanja Dekat, Lebih Hemat.</div></div>
              <div className="border-t border-dashed my-2" />
              <div className="flex justify-between"><span>{new Date(lastSale.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })}</span><span>Kasir: {lastSale.cashier_email.split("@")[0]}</span></div>
              {lastSale.offline && <div className="text-center font-black text-rose-600 border border-dashed border-rose-400 rounded my-1 py-0.5">OFFLINE — BELUM TERSINKRON KE SERVER</div>}
              <div className="border-t border-dashed my-2" />
              {lastSale.items.map((it, idx) => (
                <div key={idx}>
                  <div>{it.name.length > 20 ? it.name.slice(0, 20) : it.name}</div>
                  <div className="flex justify-between gap-2"><span className="pl-1">{rupiah(it.price)} x {it.qty}</span><span>{rupiah(it.price * it.qty)}</span></div>
                </div>
              ))}
              {lastSale.channel_entries?.length > 0 && (
                <>
                  <div className="border-t border-dashed my-2" />
                  <div className="font-bold mb-0.5">TRANSAKSI CHANNEL</div>
                  {lastSale.channel_entries.map((e, idx) => (
                    <div key={idx} className="flex justify-between gap-2">
                      <span className="flex-1">{chLabel(e.channel)} {e.jenis} ({e.arah === "in" ? "Masuk" : "Keluar"}){e.arah === "out" ? ` · serah tunai ${rupiah(e.amount)}` : ` · ${rupiah(e.amount)}`}{e.admin_fee ? ` + adm ${rupiah(e.admin_fee)}` : ""}</span>
                      <span>{rupiah(e.billed ?? (e.arah === "in" ? e.amount + e.admin_fee : e.admin_fee))}</span>
                    </div>
                  ))}
                </>
              )}
              <div className="border-t border-dashed my-2" />
              <div className="flex justify-between font-black"><span>TOTAL</span><span>{rupiah(lastSale.total)}</span></div>
              <div className="flex justify-between"><span>{lastSale.payment_method.toUpperCase()}</span><span>{rupiah(lastSale.cash_received)}</span></div>
              {lastSale.change > 0 && <div className="flex justify-between"><span>KEMBALI</span><span>{rupiah(lastSale.change)}</span></div>}
              {lastSale.member_name && <div className="mt-1">Member: {lastSale.member_name} (+{lastSale.points_earned} poin)</div>}
              <div className="border-t border-dashed my-2" />
              <div className="text-center">Terima kasih sudah belanja di FASMART!</div>
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <button data-testid="kasir-print-btn" onClick={() => { if (autoPrinted) toast.info("Struk sudah tercetak otomatis — ini cetak ulang"); window.print(); }} className="flex-1 min-w-[120px] py-2.5 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center justify-center gap-1.5"><Printer className="w-4 h-4" /> {autoPrinted ? "Cetak Ulang" : "Cetak Struk"}</button>
              <button data-testid="kasir-save-noprint-btn" onClick={saveNoPrint} title="Simpan transaksi & buka laci tanpa mencetak struk" className="flex-1 min-w-[120px] py-2.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-amber-200 transition"><Save className="w-4 h-4" /> Simpan (Tanpa Cetak)</button>
              <button data-testid="kasir-receipt-close" onClick={() => setLastSale(null)} className="flex-1 min-w-[120px] py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold">Selesai</button>
            </div>
          </div>
          <style>{`@media print { body * { visibility: hidden !important; } #receipt-print-root, #receipt-print-root * { visibility: visible !important; } body.printing-receipt > *:not(#receipt-print-root) { display: none !important; } #receipt-print-root { width: 64mm; margin: 0; padding: 1mm 2mm; background: white; font-size: 9px !important; line-height: 1.25 !important; } #receipt-print-root * { font-size: 9px !important; line-height: 1.25 !important; } #receipt-print-root .text-sm { font-size: 11px !important; } #receipt-print-root .my-2 { margin-top: 2px !important; margin-bottom: 2px !important; } #receipt-print-root .mb-2, #receipt-print-root .mt-4 { margin-bottom: 2px !important; margin-top: 2px !important; } #receipt-print-root .my-1, #receipt-print-root .mt-1 { margin-top: 1px !important; margin-bottom: 1px !important; } }`}</style>
        </div>
      )}
    </div>
  );
}
